import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { map, mergeMap, of, share, withLatestFrom } from 'rxjs';
import { select, Store } from '@ngrx/store';
import { AppState } from '../app.state';
import { PulsationModels } from '@models/pulsation.model';
import { CameraStatusActions } from '@states/camera-status/camera-status.action-types';
import { LocationSelectors } from '@states/location/location.selector-types';
import { CameraSelectors } from '@states/camera/camera.selector-types';
import { Dictionary } from '@ngrx/entity/src/models';
import { LocationStatusActions } from '@states/location-status/location-status.action-types';
import { Edge } from '../../edge/edge.model';
import { selectAllCameras } from '@states/camera/camera.selectors';
import ComponentStatusDisplay = PulsationModels.ComponentStatusDisplay;
import ComponentStatus = PulsationModels.ComponentStatus;
import { EdgeCamera } from '../../cameras/camera.model';
import { EdgeStatusActions } from '@states/edge-status/edge-status.action-types';
import { EdgeSelectors } from '@states/edge/edge.selector-types';

@Injectable()
export class DeviceStatusEffects {

  public setInitialEdgeStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EdgeStatusActions.setInitialEdgeStatus),
      withLatestFrom(
        this.store$.pipe(select(EdgeSelectors.selectAllEdges)),
      ),
      mergeMap(([{ edgeStatuses }, allEdges]) => {
          const organizationEdgeStatuses: Dictionary<Edge.V2.EdgePulsation> = Object.fromEntries(edgeStatuses.map(item => [item.edgeId, item]));

          const edgeStatusLookup: Dictionary<Edge.V2.EdgePulsationDisplay> = {};
          allEdges
            .forEach(edge => {
              edgeStatusLookup[edge.edgeId] = {
                edgeId: edge.edgeId,
                edgeStatus: organizationEdgeStatuses[edge.edgeId] ? PulsationModels.convertComponentStatusToComponentStatusDisplay(organizationEdgeStatuses[edge.edgeId]?.edgeStatus) : ComponentStatusDisplay.Unhealthy,
              };
            });
          return of(EdgeStatusActions.setInitialEdgeStatusSuccess({ edgeStatuses: Object.values(edgeStatusLookup) }));
        },
      ),
      share(),
    ),
  );


  public setInitialCameraStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        CameraStatusActions.setInitialCameraStatus,
      ),
      withLatestFrom(
        this.store$.pipe(select(selectAllCameras)),
        this.store$.pipe(select(state => state.edgeStatusState)),
      ),
      mergeMap(([{ cameraStatuses }, allOrganizationCameras, edgeStatusStore]) => {
          const organizationCameraStatuses: Dictionary<Edge.V2.CameraPulsation> = Object.fromEntries(cameraStatuses.map(item => [item.cameraId, item]));
          const organizationCameraStatusesDisplay: Dictionary<Edge.V2.CameraPulsationCalculated> = {};
          allOrganizationCameras.forEach(cameraItem => {
            const cameraId = cameraItem.edgeOnly.cameraId;
            if (!organizationCameraStatuses[cameraId]) {
              organizationCameraStatusesDisplay[cameraId] = {
                cameraId: cameraId,
                edgeId: cameraItem.edgeId,
                mainStream: ComponentStatus.NotConnected,
                cameraStatus: ComponentStatusDisplay.Unhealthy,
                storage: ComponentStatus.NotConnected,
                smartStorage: ComponentStatus.NotConnected,
                analytics: ComponentStatus.NotConnected,
                subStream: ComponentStatus.NotConnected,
                playbackStatus: ComponentStatus.NotConnected,
              };
            } else {
              organizationCameraStatusesDisplay[cameraId] = this.prepareCameraDisplayItem(organizationCameraStatuses[cameraId], edgeStatusStore.entities, cameraItem);
            }
          });

          return of(CameraStatusActions.setInitialCameraStatusSuccess({ cameraStatuses: Object.values(organizationCameraStatusesDisplay) }));
        },
      ),
      share(),
    ),
  );

  public calculateLocationStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        LocationStatusActions.calculateLocationStatus,
        CameraStatusActions.updateCameraStatusFromSocketSuccess,
        EdgeStatusActions.updateEdgeStatusFromSocketSuccess,
      ),
      withLatestFrom(
        this.store$.pipe(select(LocationSelectors.selectLocationEntities)),
        this.store$.pipe(select(state => state.cameraStatusState)),
        this.store$.pipe(select(state => state.edgeStatusState)),
        this.store$.pipe(select(CameraSelectors.selectProperFitting))
          .pipe(
            map(res => {
              const obj = {};
              res?.map(item => {
                obj[item.cameraId] = item;
              });
              return obj;
            })),
      ),
      mergeMap(([, locations, cameraStatusState, edgeStatusState, properFitting]) => {
          const locationIds = Object.keys(locations);
          const locationStatusLookup: Dictionary<Edge.V2.LocationPulsation> = {};

          locationIds.forEach(locationId => {
            const location = locations[locationId];
            const edges = Object.values(location.edges ?? {});

            /**
             * LOCATION IS ONLINE ONLY IF ALL CHILD EDGES AND CAMERAS ONLINE
             */
            const isEdgesAndCamerasAllOnline = edges.every(edge => {
              const cameras = Object.values(edge.cameras ?? {});
              if (!edgeStatusState.entities[edge.edgeId]) {
                return false;
              }
              return edgeStatusState.entities[edge.edgeId]?.edgeStatus === ComponentStatusDisplay.Online && cameras
                .every(camera => {
                  const cameraId = camera?.edgeOnly?.cameraId;
                  if (!cameraStatusState.entities[cameraId]) {
                    return false;
                  }
                  return cameraStatusState.entities[cameraId].cameraStatus === ComponentStatusDisplay.Online;
                });
            });


            if (isEdgesAndCamerasAllOnline) {
              locationStatusLookup[locationId] = {
                locationId: locationId,
                status: ComponentStatus.Online,
              };
            } else {
              /**
               * LOCATION IS OFFLINE ONLY IF ALL CHILD EDGES AND CAMERAS OFFLINE
               */
              const isEdgesAndCamerasAllOffline = edges.every(edge => {
                const cameras = Object.values(edge.cameras ?? {});
                if (!edgeStatusState.entities[edge.edgeId]) {
                  return false;
                }
                return edgeStatusState.entities[edge.edgeId]?.edgeStatus === ComponentStatusDisplay.Offline && cameras.every(camera => {
                  const cameraId = camera?.edgeOnly?.cameraId;
                  if (!cameraStatusState.entities[cameraId]) {
                    return false;
                  }
                  const edge = edgeStatusState.entities[camera.edgeId];
                  /**
                   * Handle case if edge offline. Force set camera offline.
                   */
                  let realCameraStatus = cameraStatusState.entities[cameraId].cameraStatus;
                  switch (edge?.edgeStatus) {
                    case PulsationModels.ComponentStatusDisplay.Offline:
                      realCameraStatus = PulsationModels.ComponentStatusDisplay.Offline;
                      break;
                    case PulsationModels.ComponentStatusDisplay.Unhealthy:
                      realCameraStatus = PulsationModels.ComponentStatusDisplay.Unhealthy;
                      break;
                  }
                  return realCameraStatus === ComponentStatusDisplay.Offline;
                });
              });
              if (isEdgesAndCamerasAllOffline) {
                locationStatusLookup[locationId] = {
                  locationId: locationId,
                  status: ComponentStatus.Offline,
                };
              } else {
                locationStatusLookup[locationId] = {
                  locationId: locationId,
                  status: ComponentStatus.Unknown,
                };
              }
            }


          });

          return of(LocationStatusActions.setLocationStatuses({ locationStatuses: Object.values(locationStatusLookup) }));
        },
      ),
      share(),
    ),
  );

  public updateCameraStatusFromSocket$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CameraStatusActions.updateCameraStatusFromSocket),
      withLatestFrom(
        this.store$.pipe(select(state => state.cameraStatusState)),
        this.store$.pipe(select(state => state.edgeStatusState)),
        this.store$.pipe(select(CameraSelectors.selectAllCamerasEntities)),
      ),
      mergeMap(([{ toUpdate }, cameraStatusState, edgeStatusStore, allCameraEntities]) => {
          const currentCameraStatusItem = { ...cameraStatusState.entities[toUpdate.cameraId] };
          delete currentCameraStatusItem.cameraStatus;
          const cameraStatusToUpdateItem: Edge.V2.CameraPulsation = {
            ...currentCameraStatusItem,
            ...toUpdate,
          };
          const cameraItem = allCameraEntities[toUpdate.cameraId];
          if (!cameraItem) {
            return of(CameraStatusActions.updateCameraStatusFromSocketSkip());
          }
          const updatedCameraStatusDisplay = this.prepareCameraDisplayItem(cameraStatusToUpdateItem, edgeStatusStore.entities, cameraItem);
          return of(CameraStatusActions.updateCameraStatusFromSocketSuccess({ cameraStatus: updatedCameraStatusDisplay }));
        },
      ),
      share(),
    ),
  );


  public setCameraStatusTimeout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CameraStatusActions.setCameraStatusTimeout),
      withLatestFrom(
        this.store$.pipe(select(state => state.cameraState)),
      ),
      mergeMap(([{ cameraStatus }, { entities }]) => {
          const originCamera = entities[cameraStatus.cameraId];
          return of(CameraStatusActions.setCameraStatusTimeoutSuccess({
            cameraStatus: {
              ...cameraStatus,
              edgeId: originCamera.edgeId,
            },
          }));
        },
      ),
      share(),
    ),
  );

  public updateEdgeStatusFromSocket$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EdgeStatusActions.updateEdgeStatusFromSocket),
      withLatestFrom(
        this.store$.pipe(select(state => state.edgeStatusState)),
        this.store$.pipe(select(CameraSelectors.selectAllCamerasEntities)),
      ),
      mergeMap(([{ toUpdate }, edgeStatusStore, allCameraEntities]) => {
          const currentEdgeStatusItem: Edge.V2.EdgePulsationDisplay = { ...edgeStatusStore.entities[toUpdate.edgeId] };
          const edgeStatusDisplayItem: Edge.V2.EdgePulsationDisplay = {
            ...currentEdgeStatusItem,
            edgeStatus: PulsationModels.convertComponentStatusToComponentStatusDisplay(toUpdate.edgeStatus),
          };
          return [
            EdgeStatusActions.updateEdgeStatusFromSocketSuccess({ toUpdate: edgeStatusDisplayItem }),
          ];
        },
      ),
      share(),
    ),
  );

  constructor(private actions$: Actions, private store$: Store<AppState>) {
  }

  private prepareCameraDisplayItem(toUpdate: Edge.V2.CameraPulsation, edgeStatusEntities: Dictionary<Edge.V2.EdgePulsationDisplay>, cameraItem: EdgeCamera.CameraItem) {
    const isEdgeOnline = edgeStatusEntities[toUpdate.edgeId]?.edgeStatus === ComponentStatusDisplay.Online;
    const mainStreamStatusOnline = toUpdate.mainStream === ComponentStatus.Online;

    const analyticStatusOnline = toUpdate.analytics === ComponentStatus.Online;
    const storageStatusOnline = toUpdate.storage === ComponentStatus.Online || toUpdate.storage === ComponentStatus.NotRecording;

    let statusOnlineMainCondition = mainStreamStatusOnline
      && (analyticStatusOnline || (typeof cameraItem?.edgeOnly?.analyticMode !== 'undefined' && cameraItem?.edgeOnly?.analyticMode !== EdgeCamera.AnalyticModes.Enabled))
      && storageStatusOnline
      && isEdgeOnline;

    /**
     * 18/12/2023 offline -> if stream is offline.
     * 25/03/2024 offline -> if edge is offline
     * all the other combination that today makes you offline should be changed to Unhealthy.
     */
    let statusOfflineMainCondition = toUpdate.mainStream === ComponentStatus.Offline || edgeStatusEntities[cameraItem.edgeId]?.edgeStatus === ComponentStatusDisplay.Offline;

    if (cameraItem.edgeOnly.smartStorage?.enabled) {
      const smartStorageStatus = toUpdate.smartStorage;
      statusOnlineMainCondition = statusOnlineMainCondition && smartStorageStatus === PulsationModels.ComponentStatus.Online;
    }

    if (cameraItem.edgeOnly.storageStream?.storageFromSubstream) {
      const subStreamStatus = toUpdate.subStream;
      statusOnlineMainCondition = statusOnlineMainCondition && subStreamStatus === PulsationModels.ComponentStatus.Online;
    }

    const cameraStatus: PulsationModels.ComponentStatusDisplay = statusOnlineMainCondition ? ComponentStatusDisplay.Online : (statusOfflineMainCondition ? ComponentStatusDisplay.Offline : ComponentStatusDisplay.Unhealthy);
    const updatedCameraStatusDisplay: Edge.V2.CameraPulsationCalculated = {
      cameraId: toUpdate.cameraId,
      edgeId: cameraItem.edgeId,
      cameraStatus: cameraStatus,
      mainStream: toUpdate.mainStream,
      storage: toUpdate.storage,
      smartStorage: toUpdate.smartStorage,
      analytics: toUpdate.analytics,
      subStream: toUpdate.subStream,
      playbackStatus: toUpdate.playbackStatus,
    };
    return updatedCameraStatusDisplay;
  }
}
